import { eEscrowPrepaymentsAtClosing } from '../../enums/eEscrowPrepaymentsAtClosing';
import { Logger } from '../../logging/LoggerService';
import { orderedSort } from '../../utilities/orderedSort';
import { IRequireShoppedFor } from '../interfaces/IRequireShoppedFor';
import { ITotals } from '../interfaces/ITotals';
import { generateSection } from './generateSection';
import { sortByCreatedAt } from './sortByCreatedAt';
import { sortByTypeAndSubType } from './sortByTypeAndSubType';
import { ITotalClosingCosts } from '../interfaces/ITotalClosingCosts';
import { IPurchaseTotals } from '../interfaces/IPurchaseTotals';
import { IOptionalParent } from '../interfaces/IOptionalParent';
import {
  IBuyerReimbursementToThirdPartyViewItem,
  ICDV3ViewItem,
  ISalesTaxViewItem,
  ITitleFeeViewItem,
} from '../interfaces/ICDViewItem';
import { IGroupTotals } from '../interfaces/IGroupTotals';
import { ICDViewItemGroups } from '../interfaces/ICDViewItemCollection';
import { ICDViewItemSection } from '../interfaces/ICDV3ItemSection';
import { eAdjustmentType } from '../../enums/eAdjustmentType';
import { TitleFeesThatGoInSectionH } from '../../enums/TitleFeesThatGoInSectionH';
import { eSalesTaxSubTypes } from '../../enums/eSalesTaxSubTypes';
import { eReimbursements } from '../../enums/eReimbursments';
import { eRecordingFees } from '../../enums/eRecordingFees';
import { eCDSections } from '../../enums/eCDSections';
import { eNonInterestPrepayments } from '../../enums/eNonInterestPrepayments';
import { eCDV3ItemTypes } from '../../enums/eCDV3ItemTypes';
import { convertToFloat } from '../../utilities/convertToFloat';
import { sum } from '../../utilities/sum';

function getCdSectionForTitleFee(
  item: ITitleFeeViewItem
): eCDSections.other_costs | eCDSections.services_shopped_for | eCDSections.services_not_shopped_for {
  return item.sub_type && TitleFeesThatGoInSectionH.includes(item.sub_type)
    ? eCDSections.other_costs
    : getCdSectionForItemByShoppedFor(item);
}

function getCdSectionForItemByShoppedFor(
  item: IRequireShoppedFor
): eCDSections.services_shopped_for | eCDSections.services_not_shopped_for {
  return item.shopped_for ? eCDSections.services_shopped_for : eCDSections.services_not_shopped_for;
}

const getCdSectionForSalesTaxSubType = (
  item: ISalesTaxViewItem
): eCDSections.services_shopped_for | eCDSections.services_not_shopped_for | eCDSections.other_costs => {
  switch (item.sub_type) {
    case eSalesTaxSubTypes.title_fee_lenders:
    case eSalesTaxSubTypes.title_fee_settlement:
    case eSalesTaxSubTypes.title_fee_extended_lenders_policy:
      if (item.shopped_for) {
        return eCDSections.services_shopped_for;
      } else {
        return eCDSections.services_not_shopped_for;
      }
    case eSalesTaxSubTypes.title_fee_owners:
    case eSalesTaxSubTypes.title_fee_homeowners_policy:
      return eCDSections.other_costs;
    default:
      throw new Error('Grouping Error: Can not group an unrecognized Sales Tax Subtype');
  }
};

function getCdSectionForTitlePremiumAdjustment(item: IBuyerReimbursementToThirdPartyViewItem): eCDSections[] {
  if (item.sub_type === eAdjustmentType.TitlePremiumAdjustment) {
    return [eCDSections.due_from_buyer, eCDSections.due_to_seller];
  }
  return [eCDSections.due_from_buyer];
}

type GroupedItems = { [key in eCDSections]: ICDV3ViewItem[] };

function groupBySection(items: ICDV3ViewItem[]): GroupedItems {
  /*tslint:disable:object-literal-sort-keys*/
  const cdSectionGroups: GroupedItems = {
    origination: [],
    services_not_shopped_for: [],
    services_shopped_for: [],
    recording_fees_and_taxes: [],
    prepayments: [],
    escrow_at_closing: [],
    other_costs: [],
    payoffs: [],
    adjustments: [],
    lender_credits: [],
    due_from_buyer: [],
    paid_already_by_buyer: [],
    due_to_seller: [],
    due_from_seller: [],
  };
  /*tslint:enable:object-literal-sort-keys*/
  return items.reduce((groups, item) => {
    let cdSections: eCDSections[] = [];
    switch (item.type) {
      case eCDV3ItemTypes.percentage_of_loan_amount:
      case eCDV3ItemTypes.origination_fee_other:
        cdSections = [eCDSections.origination];
        break;
      case eCDV3ItemTypes.title_fee:
        cdSections = [getCdSectionForTitleFee(item)];
        break;
      case eCDV3ItemTypes.title_fee_other:
      case eCDV3ItemTypes.other_loan_cost:
      case eCDV3ItemTypes.endorsement:
      case eCDV3ItemTypes.endorsement_other:
        cdSections = [getCdSectionForItemByShoppedFor(item)];
        break;
      case eCDV3ItemTypes.recording_fee:
      case eCDV3ItemTypes.recording_fee_other:
      case eCDV3ItemTypes.tax:
      case eCDV3ItemTypes.tax_other:
        cdSections = [eCDSections.recording_fees_and_taxes];
        break;
      case eCDV3ItemTypes.interest_prepayment:
      case eCDV3ItemTypes.non_interest_prepayment:
      case eCDV3ItemTypes.prepayment_other:
        cdSections = [eCDSections.prepayments];
        break;
      case eCDV3ItemTypes.escrow_at_closing:
      case eCDV3ItemTypes.escrow_at_closing_other:
      case eCDV3ItemTypes.aggregate_adjustment:
        cdSections = [eCDSections.escrow_at_closing];
        break;
      case eCDV3ItemTypes.other_fee:
      case eCDV3ItemTypes.hoa_fee:
      case eCDV3ItemTypes.property_taxes:
        cdSections = [eCDSections.other_costs];
        break;
      case eCDV3ItemTypes.lender_credit:
        cdSections = [eCDSections.lender_credits, eCDSections.due_from_buyer];
        break;
      case eCDV3ItemTypes.payoff:
        cdSections = [eCDSections.payoffs, eCDSections.due_from_seller];
        break;
      case eCDV3ItemTypes.buyer_reimbursement_to_third_party:
        cdSections = getCdSectionForTitlePremiumAdjustment(item);
        break;
      case eCDV3ItemTypes.sale_price_of_property:
      case eCDV3ItemTypes.personal_property_sale:
      case eCDV3ItemTypes.buyer_reimbursement_to_seller:
      case eCDV3ItemTypes.buyer_reimbursement_other_to_seller:
        cdSections = [eCDSections.due_from_buyer, eCDSections.due_to_seller];
        break;
      case eCDV3ItemTypes.buyer_deposit:
      case eCDV3ItemTypes.loan_amount:
        cdSections = [eCDSections.paid_already_by_buyer];
        break;
      case eCDV3ItemTypes.seller_excess_deposit:
        cdSections = [eCDSections.due_from_seller];
        break;
      case eCDV3ItemTypes.existing_loans_assumed:
      case eCDV3ItemTypes.seller_credit:
      case eCDV3ItemTypes.buyer_credit_from_seller:
      case eCDV3ItemTypes.buyer_credit_from_third_party:
      case eCDV3ItemTypes.buyer_reimbursement_from_seller:
      case eCDV3ItemTypes.buyer_reimbursement_other_from_seller:
        cdSections = [eCDSections.paid_already_by_buyer, eCDSections.due_from_seller];
        break;
      case eCDV3ItemTypes.sales_tax:
        cdSections = [getCdSectionForSalesTaxSubType(item)];
        break;
      case eCDV3ItemTypes.appraisal_fee:
        cdSections = [getCdSectionForItemByShoppedFor(item)];
        break;
      default:
        throw new Error('Grouping Error: Can not group an unrecognized CdItemType.');
    }
    for (const cdSection of cdSections) {
      groups[cdSection] = [...(groups[cdSection] || []), item];
    }
    if (cdSections.length === 0) {
      Logger.error(`Can not match item to CdSection`, { sections: eCDSections, item });
    }
    return groups;
  }, cdSectionGroups);
}

//the previous version of this function assumed that there would be a single tax child.
// this version finds all child taxes. if the parent is not in this list, the child is placed at the bottom of the array of CD items
const shuffleChildrenToUnderTheirParent = (items: Array<ICDV3ViewItem & IOptionalParent>) => {
  let childArray: Array<ICDV3ViewItem & IOptionalParent> = [];
  let nonChildArray: Array<ICDV3ViewItem & IOptionalParent> = [];
  items.forEach((item) => (item.parent_id !== 'undefined' ? childArray.push(item) : nonChildArray.push(item)));

  //go through the child array and splice each child into the nonChildArray
  childArray.forEach((child) => {
    const parent = nonChildArray.find((item) => child.parent_id === item.id);
    !!parent ? nonChildArray.splice(nonChildArray.indexOf(parent) + 1, 0, child) : nonChildArray.push(child);
  });
  return nonChildArray;
};

const sortGroupedItems = ({
  origination,
  services_not_shopped_for,
  services_shopped_for,
  recording_fees_and_taxes,
  prepayments,
  escrow_at_closing,
  other_costs,
  payoffs,
  adjustments,
  lender_credits,
  due_from_buyer,
  paid_already_by_buyer,
  due_to_seller,
  due_from_seller,
}: GroupedItems): GroupedItems => {
  return {
    origination: origination
      .slice()
      .sort(
        orderedSort(
          sortByTypeAndSubType([
            { type: eCDV3ItemTypes.percentage_of_loan_amount },
            { type: eCDV3ItemTypes.origination_fee_other },
          ]),
          sortByCreatedAt
        )
      ),
    services_not_shopped_for: shuffleChildrenToUnderTheirParent(services_not_shopped_for.slice().sort(sortByCreatedAt)),
    services_shopped_for: shuffleChildrenToUnderTheirParent(services_shopped_for.slice().sort(sortByCreatedAt)),
    recording_fees_and_taxes: recording_fees_and_taxes.slice().sort(
      orderedSort(
        sortByTypeAndSubType([
          {
            sub_type: eRecordingFees.deed,
            type: eCDV3ItemTypes.recording_fee,
          },
          {
            sub_type: eRecordingFees.mortgage,
            type: eCDV3ItemTypes.recording_fee,
          },
        ]),
        sortByCreatedAt
      )
    ),
    prepayments: prepayments.slice().sort(
      orderedSort(
        sortByTypeAndSubType([
          {
            sub_type: eNonInterestPrepayments.homeowners_insurance,
            type: eCDV3ItemTypes.non_interest_prepayment,
          },
          {
            sub_type: eNonInterestPrepayments.mortgage_insurance,
            type: eCDV3ItemTypes.non_interest_prepayment,
          },
          { type: eCDV3ItemTypes.interest_prepayment },
          {
            sub_type: eNonInterestPrepayments.property_tax,
            type: eCDV3ItemTypes.non_interest_prepayment,
          },
          { type: eCDV3ItemTypes.prepayment_other },
        ]),
        sortByCreatedAt
      )
    ),
    escrow_at_closing: escrow_at_closing
      .slice()
      .sort(
        orderedSort(
          sortByTypeAndSubType([
            { type: eCDV3ItemTypes.escrow_at_closing, sub_type: eEscrowPrepaymentsAtClosing.homeowners_insurance },
            { type: eCDV3ItemTypes.escrow_at_closing, sub_type: eEscrowPrepaymentsAtClosing.mortgage_insurance },
            { type: eCDV3ItemTypes.escrow_at_closing, sub_type: eEscrowPrepaymentsAtClosing.property_tax },
            { type: eCDV3ItemTypes.escrow_at_closing_other },
            { type: eCDV3ItemTypes.aggregate_adjustment },
          ]),
          sortByCreatedAt
        )
      ),
    other_costs: shuffleChildrenToUnderTheirParent(other_costs.slice().sort(sortByCreatedAt)),
    payoffs: payoffs.slice().sort(sortByCreatedAt),
    adjustments: adjustments.slice().sort(sortByCreatedAt),
    lender_credits: lender_credits.slice().sort(sortByCreatedAt),
    due_from_buyer: due_from_buyer.slice().sort(
      orderedSort(
        sortByTypeAndSubType([
          {
            type: eCDV3ItemTypes.sale_price_of_property,
          },
          {
            type: eCDV3ItemTypes.personal_property_sale,
          },
          {
            type: eCDV3ItemTypes.buyer_reimbursement_to_third_party,
          },
          {
            sub_type: eReimbursements.city_and_town_taxes,
            type: eCDV3ItemTypes.buyer_reimbursement_to_seller,
          },
          {
            sub_type: eReimbursements.county_taxes,
            type: eCDV3ItemTypes.buyer_reimbursement_to_seller,
          },
          {
            sub_type: eReimbursements.assessments,
            type: eCDV3ItemTypes.buyer_reimbursement_to_seller,
          },
          { type: eCDV3ItemTypes.buyer_reimbursement_other_to_seller },
        ]),
        sortByCreatedAt
      )
    ),
    paid_already_by_buyer: paid_already_by_buyer.slice().sort(
      orderedSort(
        sortByTypeAndSubType([
          { type: eCDV3ItemTypes.buyer_deposit },
          { type: eCDV3ItemTypes.loan_amount },
          { type: eCDV3ItemTypes.existing_loans_assumed },
          { type: eCDV3ItemTypes.seller_credit },
          { type: eCDV3ItemTypes.buyer_credit_from_seller },
          { type: eCDV3ItemTypes.buyer_credit_from_third_party },
          {
            sub_type: eReimbursements.city_and_town_taxes,
            type: eCDV3ItemTypes.buyer_reimbursement_from_seller,
          },
          {
            sub_type: eReimbursements.county_taxes,
            type: eCDV3ItemTypes.buyer_reimbursement_from_seller,
          },
          {
            sub_type: eReimbursements.assessments,
            type: eCDV3ItemTypes.buyer_reimbursement_from_seller,
          },
          { type: eCDV3ItemTypes.buyer_reimbursement_other_from_seller },
        ]),
        sortByCreatedAt
      )
    ),
    due_to_seller: due_to_seller.slice().sort(
      orderedSort(
        sortByTypeAndSubType([
          {
            type: eCDV3ItemTypes.sale_price_of_property,
          },
          {
            type: eCDV3ItemTypes.personal_property_sale,
          },
          {
            type: eCDV3ItemTypes.buyer_reimbursement_to_third_party,
          },
          {
            sub_type: eReimbursements.city_and_town_taxes,
            type: eCDV3ItemTypes.buyer_reimbursement_to_seller,
          },
          {
            sub_type: eReimbursements.county_taxes,
            type: eCDV3ItemTypes.buyer_reimbursement_to_seller,
          },
          {
            sub_type: eReimbursements.assessments,
            type: eCDV3ItemTypes.buyer_reimbursement_to_seller,
          },
          { type: eCDV3ItemTypes.buyer_reimbursement_other_to_seller },
        ]),
        sortByCreatedAt
      )
    ),
    due_from_seller: due_from_seller.slice().sort(
      orderedSort(
        sortByTypeAndSubType([
          {
            type: eCDV3ItemTypes.seller_excess_deposit,
          },
          {
            type: eCDV3ItemTypes.existing_loans_assumed,
          },
          {
            type: eCDV3ItemTypes.seller_credit,
          },
          {
            type: eCDV3ItemTypes.buyer_credit_from_seller,
          },
          {
            sub_type: eReimbursements.city_and_town_taxes,
            type: eCDV3ItemTypes.buyer_reimbursement_from_seller,
          },
          {
            sub_type: eReimbursements.county_taxes,
            type: eCDV3ItemTypes.buyer_reimbursement_from_seller,
          },
          {
            sub_type: eReimbursements.assessments,
            type: eCDV3ItemTypes.buyer_reimbursement_from_seller,
          },
          { type: eCDV3ItemTypes.buyer_reimbursement_other_from_seller },
        ]),
        sortByCreatedAt
      )
    ),
    /*tslint:enable:object-literal-sort-keys*/
  };
};

function sectionBuyerOwes(sections: ICDViewItemSection[]) {
  const buyerAtClosing = sections.map((section) => section.totals.buyer_at_closing || 0).reduce(sum, 0);
  const buyerBeforeClosing = sections.map((section) => section.totals.buyer_before_closing || 0).reduce(sum, 0);
  return (buyerBeforeClosing || 0) - (buyerAtClosing || 0);
}

function generateGroupTotals(sections: Array<ICDViewItemSection | null>): IGroupTotals {
  const notNullSections = sections.filter((section) => !!section) as ICDViewItemSection[];
  return {
    subtotals: {
      buyer_at_closing: notNullSections.map((section) => section.totals.buyer_at_closing || 0).reduce(sum, 0),
      buyer_before_closing: notNullSections.map((section) => section.totals.buyer_before_closing || 0).reduce(sum, 0),
      buyer_paid: notNullSections.map((section) => section.totals.buyer_paid || 0).reduce(sum, 0),
      paid_by_others: notNullSections.map((section) => section.totals.paid_by_others || 0).reduce(sum, 0),
      seller_at_closing: notNullSections.map((section) => section.totals.seller_at_closing || 0).reduce(sum, 0),
      seller_before_closing: notNullSections.map((section) => section.totals.seller_before_closing || 0).reduce(sum, 0),
      seller_paid: notNullSections.map((section) => section.totals.seller_paid || 0).reduce(sum, 0),
    } as ITotals,
    total: sectionBuyerOwes(notNullSections),
  };
}

function generatePurchaseTotals(
  dueFromBuyer: ICDViewItemSection | null | undefined,
  paidAlreadyByBuyer: ICDViewItemSection | null | undefined,
  dueToSeller: ICDViewItemSection | null | undefined,
  dueFromSeller: ICDViewItemSection | null | undefined,
  totals: ITotalClosingCosts
): IPurchaseTotals {
  const paidByOther = paidAlreadyByBuyer
    ? paidAlreadyByBuyer.items
        .map((item) => item.paid_by_others)
        .map(convertToFloat)
        .reduce(sum, 0)
    : 0;
  /*tslint:disable:variable-name*/
  const due_from_buyer =
    (dueFromBuyer ? dueFromBuyer.totals.buyer_paid || 0 : 0) + (totals.subtotals.buyer_at_closing || 0);
  const due_from_seller =
    (dueFromSeller ? dueFromSeller.totals.seller_at_closing || 0 : 0) + (totals.subtotals.seller_at_closing || 0);
  const due_to_seller_calc = dueToSeller
    ? (dueToSeller.totals.buyer_paid || 0) + (dueToSeller.totals.seller_paid || 0)
    : 0;

  const title_adjustment_seller_credit = dueToSeller
    ? dueToSeller.items.find(
        (it) =>
          it.type === eCDV3ItemTypes.buyer_reimbursement_to_third_party &&
          it.sub_type === eAdjustmentType.TitlePremiumAdjustment &&
          (it.seller_at_closing || 0) < 0
      )
    : null;
  const title_adjustment_seller = title_adjustment_seller_credit ? title_adjustment_seller_credit.seller_at_closing : 0;

  const paid_already_by_buyer_calc =
    (paidAlreadyByBuyer
      ? (paidAlreadyByBuyer.totals.buyer_paid || 0) + (paidAlreadyByBuyer.totals.seller_paid || 0)
      : 0) + (paidByOther || 0);

  const title_adjustment_buyer_credit = paidAlreadyByBuyer
    ? paidAlreadyByBuyer.items.find(
        (it) =>
          it.type === eCDV3ItemTypes.buyer_credit_from_seller &&
          it.sub_type === eAdjustmentType.TitlePremiumAdjustment &&
          (it.buyer_at_closing || 0) < 0
      )
    : null;
  const title_adjustment_buyer = title_adjustment_buyer_credit ? title_adjustment_buyer_credit.buyer_at_closing : 0;

  const title_adjustment_buyer_credit_from_third = paidAlreadyByBuyer
    ? paidAlreadyByBuyer.items.find(
        (it) =>
          it.type === eCDV3ItemTypes.buyer_credit_from_third_party &&
          it.sub_type === eAdjustmentType.TitlePremiumAdjustment &&
          (it.buyer_at_closing || 0) < 0
      )
    : null;
  const title_adjustment_buyer_from_third = title_adjustment_buyer_credit_from_third
    ? title_adjustment_buyer_credit_from_third.buyer_at_closing
    : 0;

  /*tslint:enable:variable-name*/
  return {
    buyer_due:
      due_from_buyer -
      (paid_already_by_buyer_calc - (title_adjustment_buyer || 0) - (title_adjustment_buyer_from_third || 0)),
    due_from_buyer,
    due_from_seller,
    due_to_seller: due_to_seller_calc - (title_adjustment_seller || 0),
    paid_already_by_buyer:
      paid_already_by_buyer_calc - (title_adjustment_buyer || 0) - (title_adjustment_buyer_from_third || 0),
    seller_due: due_to_seller_calc - (title_adjustment_seller || 0) - due_from_seller,
  };
}

function groupTotalBuyerOwes(groupTotals: IGroupTotals[]) {
  const buyerBeforeClosing = groupTotals
    .map((total) => (total ? total.subtotals.buyer_before_closing : 0))
    .reduce(sum, 0);
  const buyerAtClosing = groupTotals.map((total) => (total ? total.subtotals.buyer_at_closing : 0)).reduce(sum, 0);
  return (buyerAtClosing || 0) - (buyerBeforeClosing || 0);
}

function generateTotalClosingCosts(
  groupTotals: Array<IGroupTotals | null>,
  lenderCredits?: Array<ICDV3ViewItem | null> | null
): ITotalClosingCosts {
  const notNullGroupTotals = groupTotals.filter((groupTotal) => !!groupTotal) as IGroupTotals[];
  const lenderCreditsTotal = lenderCredits
    ? lenderCredits
        .map((credit) => (credit ? credit.buyer_at_closing : 0))
        .map(convertToFloat)
        .reduce(sum, 0)
    : 0;
  return {
    lender_credits: lenderCreditsTotal,
    subtotals: {
      buyer_at_closing: notNullGroupTotals.map((groupTotal) => groupTotal.subtotals.buyer_at_closing).reduce(sum, 0),
      buyer_before_closing: notNullGroupTotals
        .map((groupTotal) => groupTotal.subtotals.buyer_before_closing)
        .reduce(sum, 0),
      buyer_paid:
        (notNullGroupTotals.map((groupTotal) => groupTotal.subtotals.buyer_paid).reduce(sum, 0) as number) +
        (lenderCreditsTotal || 0),
      paid_by_others: notNullGroupTotals.map((groupTotal) => groupTotal.subtotals.paid_by_others).reduce(sum, 0),
      seller_at_closing: notNullGroupTotals.map((groupTotal) => groupTotal.subtotals.seller_at_closing).reduce(sum, 0),
      seller_before_closing: notNullGroupTotals
        .map((groupTotal) => groupTotal.subtotals.seller_before_closing)
        .reduce(sum, 0),
      seller_paid: notNullGroupTotals.map((groupTotal) => groupTotal.subtotals.seller_paid).reduce(sum, 0),
    },
    total: groupTotalBuyerOwes(notNullGroupTotals),
  };
}

const pipe =
  (...fns: Array<(param: any) => any>) =>
  (x: any) =>
    fns.reduce((v, f) => f(v), x);

export const generateCDViewItemCollection = (items: ICDV3ViewItem[]): ICDViewItemGroups => {
  const groups: GroupedItems = pipe(groupBySection, sortGroupedItems)(items);
  const escrow_at_closing = generateSection(groups.escrow_at_closing);
  const origination = generateSection(groups.origination);
  const other_costs = generateSection(groups.other_costs);
  const prepaids = generateSection(groups.prepayments);
  const recording_fees_and_taxes = generateSection(groups.recording_fees_and_taxes);
  const services_not_shopped_for = generateSection(groups.services_not_shopped_for);
  const services_shopped_for = generateSection(groups.services_shopped_for);
  const payoffs = groups.payoffs;
  const adjustments = groups.adjustments;
  const lender_credits = groups.lender_credits;
  const due_from_buyer = generateSection(groups.due_from_buyer);
  const paid_already_by_buyer = generateSection(groups.paid_already_by_buyer);
  const due_to_seller = generateSection(groups.due_to_seller);
  const due_from_seller = generateSection(groups.due_from_seller);

  const total_loan_costs = generateGroupTotals([origination, services_not_shopped_for, services_shopped_for]);
  const total_other_costs = generateGroupTotals([recording_fees_and_taxes, prepaids, escrow_at_closing, other_costs]);
  const total_closing_costs = generateTotalClosingCosts([total_loan_costs, total_other_costs], lender_credits);
  const total_purchase_costs = generatePurchaseTotals(
    due_from_buyer,
    paid_already_by_buyer,
    due_to_seller,
    due_from_seller,
    total_closing_costs
  );
  /*tslint:enable:variable-name*/

  return {
    adjustments,
    asArray: items,
    due_from_buyer,
    due_from_seller,
    due_to_seller,
    escrow_at_closing,
    lender_credits,
    origination,
    other_costs,
    paid_already_by_buyer,
    payoffs,
    prepaids,
    recording_fees_and_taxes,
    services_not_shopped_for,
    services_shopped_for,
    total_closing_costs,
    total_loan_costs,
    total_other_costs,
    total_purchase_costs,
  };
  /*tslint:enable:variable-name*/
};
